/**
 * Security & Compliance Utilities
 */

import {
  SecurityMetadata,
  SecurityZone,
  DataClassification,
  ComplianceFramework,
  ThreatLevel,
  Vulnerability,
  ExtendedArchiMateDiagram,
} from './extended-types';
import { ArchiMateElement, ArchiMateRelationship } from './types';

/**
 * Create default security metadata with all required fields
 */
export function createDefaultSecurityMetadata(): SecurityMetadata {
  return {
    zone: 'trusted',
    dataClassification: 'internal',
    handlesPersonalData: false,
    handlesFinancialData: false,
    handlesHealthData: false,
    encryption: {
      atRest: false,
      inTransit: false,
    },
    authentication: {
      required: false,
      type: 'none',
      context: 'end-user',
      mfaRequired: false,
    },
    authorization: {
      model: 'none',
      leastPrivilege: false,
    },
    complianceFrameworks: [],
    threatLevel: 'low',
    internetFacing: false,
    logging: {
      enabled: false,
      piiRedaction: false,
      auditTrail: false,
    },
    monitoring: {
      enabled: false,
      alerting: false,
      siem: false,
    },
    backups: {
      enabled: false,
      encryption: false,
      offsite: false,
      tested: false,
    },
    network: {
      firewalled: false,
      segmented: false,
      vpnRequired: false,
    },
    threats: [],
    mitigations: [],
  };
}

export function calculateSecurityScore(metadata: SecurityMetadata): number {
  let score = 100;

  // Security Zone Impact - Critical zones need higher scores
  // Lower scores indicate higher risk exposure
  switch (metadata.zone) {
    case 'public':
      score -= 25; // Highest risk - directly exposed
      break;
    case 'dmz':
      score -= 15; // High risk - semi-exposed
      break;
    case 'trusted':
      score -= 5; // Moderate risk
      break;
    case 'restricted':
      score += 5; // Lower risk - additional controls
      break;
    case 'critical':
      // Critical zone should have perfect security
      // We'll penalize heavily if controls are missing
      break;
  }

  // Internet-facing components need stronger controls
  if (metadata.internetFacing) {
    score -= 15;

    // Check for attack surface protections
    if (metadata.attackSurface) {
      if (!metadata.attackSurface.inputValidation) score -= 10;
      if (!metadata.attackSurface.rateLimiting) score -= 8;
      if (!metadata.attackSurface.ddosProtection) score -= 7;
    } else {
      // No attack surface defined for internet-facing component
      score -= 15;
    }
  }

  // Data Classification Impact
  switch (metadata.dataClassification) {
    case 'pci':
      score -= 20; // Payment data requires strict controls
      break;
    case 'pii':
      score -= 18; // Personal data requires strong protection
      break;
    case 'confidential':
      score -= 12;
      break;
    case 'internal':
      score -= 5;
      break;
    case 'public':
      // No penalty
      break;
  }

  // Sensitive Data Handling
  if (metadata.handlesPersonalData && !metadata.encryption.atRest) score -= 20;
  if (metadata.handlesFinancialData && !metadata.encryption.atRest) score -= 25;
  if (metadata.handlesHealthData && !metadata.encryption.atRest) score -= 22;

  // Encryption - More detailed scoring
  if (!metadata.encryption.atRest) {
    score -= 15;
  } else if (metadata.encryption.atRestAlgorithm) {
    // Bonus for specifying algorithm (shows due diligence)
    score += 2;
  }

  if (!metadata.encryption.inTransit) {
    score -= 15;
  } else if (metadata.encryption.inTransitProtocol?.includes('TLS 1.3')) {
    score += 2; // Modern protocol
  } else if (metadata.encryption.inTransitProtocol?.includes('TLS 1.2')) {
    score += 1;
  }

  // Key management
  if (metadata.encryption.keyManagement) {
    score += 3; // Proper key management
  }

  // Authentication & Authorization
  const auth = metadata.authentication || {
    required: metadata.authenticationRequired || false,
    type: 'none',
    context: 'end-user',
    mfaRequired: metadata.mfa || false,
  };

  if (!auth.required) {
    score -= 20;
  } else {
    // Score based on authentication strength
    switch (auth.type) {
      case 'none':
        score -= 20;
        break;
      case 'basic':
        score -= 12; // Weakest
        break;
      case 'api-key':
        score -= 8;
        break;
      case 'jwt':
        score -= 4;
        break;
      case 'oauth2':
        score -= 2;
        break;
      case 'saml':
        score -= 1;
        break;
      case 'mutual-tls':
        score += 2; // Strongest
        break;
    }

    // MFA is critical for admin and end-user contexts
    if (!auth.mfaRequired) {
      if (auth.context === 'admin') {
        score -= 15; // Admins MUST have MFA
      } else if (auth.context === 'end-user') {
        score -= 10;
      } else if (auth.context === 'mixed') {
        score -= 12;
      }
      // Service-to-service often uses certificates/mutual TLS instead
    } else {
      score += 3; // Bonus for MFA
    }
  }

  // Authorization
  if (metadata.authorization) {
    if (metadata.authorization.model === 'none') {
      score -= 15;
    } else if (metadata.authorization.leastPrivilege) {
      score += 3; // Following best practices
    }
  }

  // Threat Level Assessment
  switch (metadata.threatLevel) {
    case 'critical':
      score -= 25;
      break;
    case 'high':
      score -= 15;
      break;
    case 'medium':
      score -= 5;
      break;
  }

  // Logging & Monitoring
  if (metadata.logging) {
    if (!metadata.logging.enabled) {
      score -= 10;
    } else {
      score += 3; // Base bonus for having logging
      if (metadata.logging.auditTrail) score += 2;
      if (metadata.logging.piiRedaction) score += 2;
    }
  } else {
    score -= 10; // No logging config
  }

  if (metadata.monitoring) {
    if (!metadata.monitoring.enabled) {
      score -= 8;
    } else {
      score += 2; // Base bonus for having monitoring
      if (metadata.monitoring.alerting) score += 2;
      if (metadata.monitoring.siem) score += 3;
    }
  } else {
    score -= 8;
  }

  // Network Security
  if (metadata.network) {
    if (metadata.network.firewalled) {
      score += 5; // Base bonus for firewall
    } else {
      score -= 12;
    }
    if (metadata.network.segmented) score += 3;
    if (metadata.network.vpnRequired) score += 2;
  }

  // Backups & DR
  if (metadata.backups) {
    if (!metadata.backups.enabled) {
      score -= 10;
    } else {
      score += 3; // Base bonus for having backups
      if (metadata.backups.encryption) score += 2;
      else score -= 5;
      if (metadata.backups.offsite) score += 2;
      else score -= 4;
      if (metadata.backups.tested) score += 3;

      // Bonus for backup frequency
      if (metadata.backups.frequency === 'Continuous') score += 2;
      else if (metadata.backups.frequency === 'Hourly') score += 1;
    }
  }

  // Security Controls Presence
  if (metadata.securityControls) {
    const totalControls =
      (metadata.securityControls.preventive?.length || 0) +
      (metadata.securityControls.detective?.length || 0) +
      (metadata.securityControls.corrective?.length || 0);

    if (totalControls >= 10) score += 5;
    else if (totalControls >= 5) score += 3;
    else if (totalControls >= 2) score += 1;
  }

  // Vulnerabilities (most critical)
  if (metadata.vulnerabilities) {
    metadata.vulnerabilities.forEach((vuln) => {
      if (vuln.status !== 'resolved') {
        switch (vuln.severity) {
          case 'critical':
            score -= 30; // Increased from 20
            break;
          case 'high':
            score -= 15; // Increased from 10
            break;
          case 'medium':
            score -= 8; // Increased from 5
            break;
          case 'low':
            score -= 3; // Increased from 2
            break;
        }
      }
    });
  }

  // Security review bonus (not required, but encouraged)
  if (metadata.lastSecurityReview) {
    // Check if review is recent (within 1 year)
    const reviewDate = new Date(metadata.lastSecurityReview);
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

    if (reviewDate >= oneYearAgo) {
      score += 3; // Recent review is a positive signal
    }
  }

  // Critical zone penalty if controls are insufficient
  if (metadata.zone === 'critical') {
    // Critical zones should have near-perfect security
    if (score < 90) {
      score -= 10; // Extra penalty for critical zone with weak security
    }
  }

  return Math.max(0, Math.min(100, score));
}

export function calculateDiagramSecurityScore(diagram: ExtendedArchiMateDiagram): number {
  let totalScore = 0;
  let count = 0;

  diagram.elements.forEach((element) => {
    const metadata = diagram.elementMetadata[element.id]?.security;
    if (metadata) {
      totalScore += calculateSecurityScore(metadata);
      count++;
    }
  });

  return count > 0 ? totalScore / count : 0;
}

export function getElementsBySecurityZone(
  diagram: ExtendedArchiMateDiagram,
  zone: SecurityZone
): ArchiMateElement[] {
  return diagram.elements.filter(
    (element) => diagram.elementMetadata[element.id]?.security?.zone === zone
  );
}

export function getElementsByDataClassification(
  diagram: ExtendedArchiMateDiagram,
  classification: DataClassification
): ArchiMateElement[] {
  return diagram.elements.filter(
    (element) =>
      diagram.elementMetadata[element.id]?.security?.dataClassification === classification
  );
}

export function getVulnerableElements(diagram: ExtendedArchiMateDiagram): Array<{
  element: ArchiMateElement;
  vulnerabilities: Vulnerability[];
}> {
  const vulnerable: Array<{ element: ArchiMateElement; vulnerabilities: Vulnerability[] }> = [];

  diagram.elements.forEach((element) => {
    const security = diagram.elementMetadata[element.id]?.security;
    if (security?.vulnerabilities && security.vulnerabilities.length > 0) {
      const openVulns = security.vulnerabilities.filter((v) => v.status !== 'resolved');
      if (openVulns.length > 0) {
        vulnerable.push({ element, vulnerabilities: openVulns });
      }
    }
  });

  return vulnerable.sort((a, b) => {
    const getSeverityValue = (severity: string) => {
      switch (severity) {
        case 'critical':
          return 4;
        case 'high':
          return 3;
        case 'medium':
          return 2;
        case 'low':
          return 1;
        default:
          return 0;
      }
    };

    const maxSevA = Math.max(...a.vulnerabilities.map((v) => getSeverityValue(v.severity)));
    const maxSevB = Math.max(...b.vulnerabilities.map((v) => getSeverityValue(v.severity)));

    return maxSevB - maxSevA;
  });
}

export function getComplianceGaps(
  diagram: ExtendedArchiMateDiagram,
  framework: ComplianceFramework
): Array<{ element: ArchiMateElement; gaps: string[] }> {
  const gaps: Array<{ element: ArchiMateElement; gaps: string[] }> = [];

  diagram.elements.forEach((element) => {
    const security = diagram.elementMetadata[element.id]?.security;
    if (!security) {
      gaps.push({ element, gaps: ['No security metadata defined'] });
      return;
    }

    const elementGaps: string[] = [];

    // Check framework-specific requirements
    if (framework === 'GDPR' || framework === 'HIPAA') {
      if (
        security.dataClassification === 'pii' ||
        security.dataClassification === 'confidential'
      ) {
        if (!security.encryption.atRest) elementGaps.push('Missing encryption at rest');
        if (!security.encryption.inTransit) elementGaps.push('Missing encryption in transit');
        if (!security.authenticationRequired) elementGaps.push('Authentication not required');
        if (security.authenticationRequired && !security.mfa)
          elementGaps.push('MFA not enabled');
      }
    }

    if (framework === 'SOC2' || framework === 'ISO27001') {
      if (!security.lastSecurityReview) {
        elementGaps.push('No security review recorded');
      } else {
        const reviewDate = new Date(security.lastSecurityReview);
        const now = new Date();
        const monthsSinceReview =
          (now.getTime() - reviewDate.getTime()) / (1000 * 60 * 60 * 24 * 30);
        if (monthsSinceReview > 12) {
          elementGaps.push('Security review overdue (>12 months)');
        }
      }

      if (security.internetFacing && security.threatLevel === 'low') {
        elementGaps.push('Internet-facing component with low threat assessment');
      }
    }

    if (elementGaps.length > 0) {
      gaps.push({ element, gaps: elementGaps });
    }
  });

  return gaps;
}

export function detectUnencryptedDataFlows(
  diagram: ExtendedArchiMateDiagram
): Array<{
  relationship: ArchiMateRelationship;
  source: ArchiMateElement;
  target: ArchiMateElement;
  reason: string;
}> {
  const unencrypted: Array<{
    relationship: ArchiMateRelationship;
    source: ArchiMateElement;
    target: ArchiMateElement;
    reason: string;
  }> = [];

  diagram.relationships.forEach((rel) => {
    if (rel.type !== 'flow' && rel.type !== 'access') return;

    const source = diagram.elements.find((e) => e.id === rel.sourceId);
    const target = diagram.elements.find((e) => e.id === rel.targetId);

    if (!source || !target) return;

    const sourceZone = diagram.elementMetadata[source.id]?.security?.zone;
    const targetZone = diagram.elementMetadata[target.id]?.security?.zone;
    const relMetadata = diagram.relationshipMetadata[rel.id]?.security;

    // Check if crossing security boundaries without encryption
    if (sourceZone && targetZone && sourceZone !== targetZone) {
      if (!relMetadata?.encryption?.inTransit) {
        unencrypted.push({
          relationship: rel,
          source,
          target,
          reason: `Unencrypted data flow between ${sourceZone} and ${targetZone} zones`,
        });
      }
    }

    // Check for sensitive data
    const sourceClassification = diagram.elementMetadata[source.id]?.security?.dataClassification;
    if (
      (sourceClassification === 'pii' || sourceClassification === 'confidential') &&
      !relMetadata?.encryption?.inTransit
    ) {
      unencrypted.push({
        relationship: rel,
        source,
        target,
        reason: `Unencrypted ${sourceClassification} data flow`,
      });
    }
  });

  return unencrypted;
}

export function getSecurityZoneColor(zone: SecurityZone): string {
  switch (zone) {
    case 'public':
      return '#ef4444'; // red
    case 'dmz':
      return '#f97316'; // orange
    case 'trusted':
      return '#eab308'; // yellow
    case 'restricted':
      return '#3b82f6'; // blue
    case 'critical':
      return '#8b5cf6'; // purple
  }
}

export function getThreatLevelColor(level: ThreatLevel): string {
  switch (level) {
    case 'low':
      return '#10b981'; // green
    case 'medium':
      return '#eab308'; // yellow
    case 'high':
      return '#f97316'; // orange
    case 'critical':
      return '#ef4444'; // red
  }
}

export function generateSecurityReport(diagram: ExtendedArchiMateDiagram): string {
  let report = `# Security & Compliance Report\n\n`;
  report += `## ${diagram.name}\n\n`;
  report += `**Generated:** ${new Date().toISOString()}\n\n`;

  // Overall score
  const score = calculateDiagramSecurityScore(diagram);
  report += `## Overall Security Score: ${score.toFixed(1)}/100\n\n`;

  // Elements by zone
  report += `## Security Zones\n\n`;
  ['public', 'dmz', 'trusted', 'restricted', 'critical'].forEach((zone) => {
    const elements = getElementsBySecurityZone(diagram, zone as SecurityZone);
    report += `- **${zone}**: ${elements.length} elements\n`;
  });

  // Vulnerabilities
  report += `\n## Vulnerabilities\n\n`;
  const vulnerable = getVulnerableElements(diagram);
  if (vulnerable.length === 0) {
    report += `No open vulnerabilities found.\n`;
  } else {
    report += `Found ${vulnerable.length} elements with vulnerabilities:\n\n`;
    vulnerable.forEach(({ element, vulnerabilities }) => {
      report += `### ${element.name}\n`;
      vulnerabilities.forEach((vuln) => {
        report += `- **${vuln.severity.toUpperCase()}**: ${vuln.description}`;
        if (vuln.cve) report += ` (${vuln.cve})`;
        report += `\n`;
      });
      report += `\n`;
    });
  }

  // Unencrypted flows
  report += `## Unencrypted Data Flows\n\n`;
  const unencrypted = detectUnencryptedDataFlows(diagram);
  if (unencrypted.length === 0) {
    report += `All data flows are encrypted.\n`;
  } else {
    report += `Found ${unencrypted.length} unencrypted data flows:\n\n`;
    unencrypted.forEach(({ source, target, reason }) => {
      report += `- ${source.name} → ${target.name}: ${reason}\n`;
    });
  }

  return report;
}

export function applySTRIDEAnalysis(
  element: ArchiMateElement,
  metadata: SecurityMetadata
): string[] {
  const threats: string[] = [];

  // Spoofing
  if (!metadata.authenticationRequired) {
    threats.push('Spoofing: No authentication required');
  }

  // Tampering
  if (!metadata.encryption.atRest) {
    threats.push('Tampering: Data not encrypted at rest');
  }

  // Repudiation
  if (metadata.zone === 'public' || metadata.zone === 'dmz') {
    threats.push('Repudiation: Consider audit logging for public-facing components');
  }

  // Information Disclosure
  if (
    metadata.dataClassification !== 'public' &&
    (!metadata.encryption.atRest || !metadata.encryption.inTransit)
  ) {
    threats.push('Information Disclosure: Sensitive data not fully encrypted');
  }

  // Denial of Service
  if (metadata.internetFacing) {
    threats.push('Denial of Service: Internet-facing component vulnerable to DoS attacks');
  }

  // Elevation of Privilege
  if (metadata.authenticationRequired && !metadata.mfa) {
    threats.push('Elevation of Privilege: MFA not enabled');
  }

  return threats;
}

/**
 * Get compliance framework requirements for a specific framework
 * Returns the key requirements that components must meet
 */
export function getComplianceFrameworkRequirements(framework: ComplianceFramework): {
  name: string;
  description: string;
  keyRequirements: Array<{
    id: string;
    requirement: string;
    category: string;
    check: (metadata: SecurityMetadata) => boolean;
  }>;
} {
  switch (framework) {
    case 'SOC2':
      return {
        name: 'SOC 2 Type II',
        description: 'Trust Services Criteria for service organizations',
        keyRequirements: [
          {
            id: 'CC6.1',
            requirement: 'Logical and physical access controls',
            category: 'Access Control',
            check: (m) =>
              m.authentication?.required &&
              m.authorization?.model !== 'none' &&
              m.network?.firewalled,
          },
          {
            id: 'CC6.6',
            requirement: 'Encryption of data at rest and in transit',
            category: 'Data Protection',
            check: (m) => m.encryption.atRest && m.encryption.inTransit,
          },
          {
            id: 'CC6.7',
            requirement: 'Transmission of data is protected',
            category: 'Data Protection',
            check: (m) => m.encryption.inTransit,
          },
          {
            id: 'CC7.2',
            requirement: 'System monitoring and logging',
            category: 'Monitoring',
            check: (m) => m.logging?.enabled && m.monitoring?.enabled,
          },
          {
            id: 'CC8.1',
            requirement: 'Change management and version control',
            category: 'Change Management',
            check: () => true, // Manual check
          },
        ],
      };

    case 'ISO27001':
      return {
        name: 'ISO 27001',
        description: 'Information security management system standard',
        keyRequirements: [
          {
            id: 'A.9.2.1',
            requirement: 'User registration and de-registration',
            category: 'Access Control',
            check: (m) => m.authentication?.required,
          },
          {
            id: 'A.9.4.1',
            requirement: 'Information access restriction',
            category: 'Access Control',
            check: (m) => m.authorization?.leastPrivilege,
          },
          {
            id: 'A.10.1.1',
            requirement: 'Cryptographic controls policy',
            category: 'Cryptography',
            check: (m) => m.encryption.atRest && m.encryption.inTransit,
          },
          {
            id: 'A.12.4.1',
            requirement: 'Event logging',
            category: 'Logging',
            check: (m) => m.logging?.enabled && m.logging?.auditTrail,
          },
          {
            id: 'A.17.1.1',
            requirement: 'Business continuity and backups',
            category: 'Business Continuity',
            check: (m) => m.backups?.enabled && m.backups?.tested,
          },
        ],
      };

    case 'GDPR':
      return {
        name: 'GDPR',
        description: 'General Data Protection Regulation (EU)',
        keyRequirements: [
          {
            id: 'Art.25',
            requirement: 'Data protection by design and default',
            category: 'Privacy by Design',
            check: (m) => m.authorization?.leastPrivilege && m.logging?.piiRedaction,
          },
          {
            id: 'Art.32',
            requirement: 'Security of processing (encryption)',
            category: 'Data Security',
            check: (m) => m.encryption.atRest && m.encryption.inTransit && m.handlesPersonalData,
          },
          {
            id: 'Art.32',
            requirement: 'Pseudonymization and encryption of personal data',
            category: 'Data Protection',
            check: (m) =>
              !m.handlesPersonalData || (m.encryption.atRest && m.logging?.piiRedaction),
          },
          {
            id: 'Art.33',
            requirement: 'Breach notification capabilities',
            category: 'Incident Response',
            check: (m) => m.monitoring?.alerting && m.logging?.enabled,
          },
          {
            id: 'Art.30',
            requirement: 'Records of processing activities',
            category: 'Documentation',
            check: (m) => m.logging?.auditTrail,
          },
        ],
      };

    case 'HIPAA':
      return {
        name: 'HIPAA',
        description: 'Health Insurance Portability and Accountability Act',
        keyRequirements: [
          {
            id: '164.312(a)(1)',
            requirement: 'Access control - unique user identification',
            category: 'Access Control',
            check: (m) => m.authentication?.required && m.authentication?.type !== 'basic',
          },
          {
            id: '164.312(a)(2)(i)',
            requirement: 'Emergency access procedure',
            category: 'Access Control',
            check: () => true, // Manual policy check
          },
          {
            id: '164.312(e)(1)',
            requirement: 'Transmission security',
            category: 'Data Protection',
            check: (m) => m.encryption.inTransit,
          },
          {
            id: '164.312(e)(2)(ii)',
            requirement: 'Encryption of ePHI',
            category: 'Data Protection',
            check: (m) => !m.handlesHealthData || (m.encryption.atRest && m.encryption.inTransit),
          },
          {
            id: '164.308(a)(1)(ii)(D)',
            requirement: 'Information system activity review',
            category: 'Audit Controls',
            check: (m) => m.logging?.enabled && m.logging?.auditTrail && m.monitoring?.enabled,
          },
        ],
      };

    case 'PCI-DSS':
      return {
        name: 'PCI-DSS',
        description: 'Payment Card Industry Data Security Standard',
        keyRequirements: [
          {
            id: 'Req.2',
            requirement: 'Do not use vendor-supplied defaults',
            category: 'Configuration',
            check: () => true, // Manual check
          },
          {
            id: 'Req.3',
            requirement: 'Protect stored cardholder data',
            category: 'Data Protection',
            check: (m) => !m.handlesFinancialData || m.encryption.atRest,
          },
          {
            id: 'Req.4',
            requirement: 'Encrypt transmission of cardholder data',
            category: 'Data Protection',
            check: (m) => !m.handlesFinancialData || m.encryption.inTransit,
          },
          {
            id: 'Req.7',
            requirement: 'Restrict access by business need-to-know',
            category: 'Access Control',
            check: (m) => m.authorization?.leastPrivilege,
          },
          {
            id: 'Req.8',
            requirement: 'Identify and authenticate access',
            category: 'Authentication',
            check: (m) => m.authentication?.required && m.authentication?.mfaRequired,
          },
          {
            id: 'Req.10',
            requirement: 'Track and monitor all access',
            category: 'Logging',
            check: (m) => m.logging?.enabled && m.logging?.auditTrail,
          },
        ],
      };

    case 'NIST':
      return {
        name: 'NIST Cybersecurity Framework',
        description: 'Framework for improving critical infrastructure cybersecurity',
        keyRequirements: [
          {
            id: 'ID.AM',
            requirement: 'Asset management',
            category: 'Identify',
            check: () => true, // Component exists
          },
          {
            id: 'PR.AC',
            requirement: 'Identity management and access control',
            category: 'Protect',
            check: (m) => m.authentication?.required && m.authorization?.model !== 'none',
          },
          {
            id: 'PR.DS',
            requirement: 'Data security (encryption)',
            category: 'Protect',
            check: (m) => m.encryption.atRest && m.encryption.inTransit,
          },
          {
            id: 'DE.CM',
            requirement: 'Security continuous monitoring',
            category: 'Detect',
            check: (m) => m.monitoring?.enabled,
          },
          {
            id: 'RS.AN',
            requirement: 'Analysis - forensic analysis',
            category: 'Respond',
            check: (m) => m.logging?.enabled && m.logging?.auditTrail,
          },
          {
            id: 'RC.RP',
            requirement: 'Recovery planning',
            category: 'Recover',
            check: (m) => m.backups?.enabled,
          },
        ],
      };

    case 'FedRAMP':
      return {
        name: 'FedRAMP',
        description: 'Federal Risk and Authorization Management Program',
        keyRequirements: [
          {
            id: 'AC-2',
            requirement: 'Account management',
            category: 'Access Control',
            check: (m) => m.authentication?.required,
          },
          {
            id: 'AC-17',
            requirement: 'Remote access',
            category: 'Access Control',
            check: (m) => m.network?.vpnRequired || !m.internetFacing,
          },
          {
            id: 'SC-13',
            requirement: 'Cryptographic protection',
            category: 'System and Communications Protection',
            check: (m) => m.encryption.atRest && m.encryption.inTransit,
          },
          {
            id: 'AU-2',
            requirement: 'Audit events',
            category: 'Audit and Accountability',
            check: (m) => m.logging?.enabled && m.logging?.auditTrail,
          },
          {
            id: 'CP-9',
            requirement: 'Information system backup',
            category: 'Contingency Planning',
            check: (m) => m.backups?.enabled && m.backups?.encryption && m.backups?.offsite,
          },
        ],
      };

    case 'CIS':
      return {
        name: 'CIS Controls',
        description: 'Center for Internet Security Critical Security Controls',
        keyRequirements: [
          {
            id: 'CIS-3',
            requirement: 'Data protection',
            category: 'Basic',
            check: (m) => m.encryption.atRest && m.encryption.inTransit,
          },
          {
            id: 'CIS-4',
            requirement: 'Secure configuration',
            category: 'Basic',
            check: () => true, // Manual check
          },
          {
            id: 'CIS-5',
            requirement: 'Account management',
            category: 'Basic',
            check: (m) => m.authentication?.required,
          },
          {
            id: 'CIS-6',
            requirement: 'Access control management',
            category: 'Basic',
            check: (m) => m.authorization?.leastPrivilege,
          },
          {
            id: 'CIS-8',
            requirement: 'Audit log management',
            category: 'Foundational',
            check: (m) => m.logging?.enabled && m.logging?.auditTrail,
          },
          {
            id: 'CIS-10',
            requirement: 'Malware defenses',
            category: 'Foundational',
            check: () => true, // Manual check
          },
        ],
      };

    default:
      return {
        name: framework,
        description: 'Compliance framework',
        keyRequirements: [],
      };
  }
}
